import React from 'react'
import Assignment from './assignment'
import { useDrop } from 'react-dnd'
import { useMutation, useQuery } from '@apollo/client'
import fetchAssignments from '../queries/assignments'
import createAssignment from '../mutations/create-assignment'
import sortAssignments from '../mutations/sort-assignments'
import deleteAssignment from '../mutations/delete-assignment'
import update from 'immutability-helper'
import debounce from 'debounce'
import { sortBy, prop } from 'ramda'

// const queueTarget = {
//   canDrop(props) {
//     const { data, metaReflection } = props
//     return (
//       !metaReflection ||
//       (data && data.assignments && data.assignments.length === 0)
//     )
//   },
//
//   drop(props, monitor) {
//     if (monitor.didDrop()) {
//       return // don't do anything if an assignment target already handled drop
//     }
//
//     const item = monitor.getItem()
//
//     if (item.cardType === 'question') {
//       const questionId = item.id
//       const { createAssignment, data, projectId, metaReflection } = props
//       const { refetch } = data
//
//       createAssignment({
//         variables: {
//           projectId,
//           questionId,
//           metaReflection,
//         },
//       }).then(refetch)
//     }
//   },
// }

// const collect = (connect, monitor) => {
//   return {
//     connectDropTarget: connect.dropTarget(),
//     isOver: monitor.isOver(),
//   }
// }

function ProjectQueue(props) {
  const [assignments, setAssignments] = React.useState([])
  const [activeAssignment, setActiveAssignment] = React.useState(
    props.activeAssignment
  )
  const { data, loading, error, refetch } = useQuery(fetchAssignments, {
    variables: {
      projectId: props.projectId,
      metaReflection: props.metaReflection,
    },
  })
  const [createAssignmentMutation] = useMutation(createAssignment)
  const [deleteAssignmentMutation] = useMutation(deleteAssignment)
  const [sortAssignmentsMutation] = useMutation(sortAssignments)

  React.useEffect(() => {
    if (!props.activeAssignment && activeAssignment) {
      refetch()
    }

    const sortBySortPosition = sortBy(prop('sortPosition'))
    const newAssignments =
      (data && data.assignments && sortBySortPosition(data.assignments)) || []

    setAssignments(newAssignments)
    setActiveAssignment(props.activeAssignment)
  }, [activeAssignment, props.activeAssignment, data])

  const [{ isOver }, drop] = useDrop({
    accept: 'question',
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return // don't do anything if an assignment target already handled drop
      }

      if (item.cardType === 'question') {
        const questionId = item.id
        const { createAssignment, data, projectId, metaReflection } = props

        createAssignmentMutation({
          variables: {
            projectId,
            questionId,
            metaReflection,
          },
        }).then(refetch)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  const canDrop = () => {
    const { metaReflection } = props
    return (
      !metaReflection ||
      (data && data.assignments && data.assignments.length === 0)
    )
  }

  const handleCreate = (questionId, hoverIndex) => {
    const { createAssignment, projectId, metaReflection } = props

    createAssignmentMutation({
      variables: {
        projectId,
        questionId,
        metaReflection,
      },
    }).then(() => {
      refetch().then(() => {
        handleSort(assignments.length, hoverIndex)
      })
    })
  }

  const handleDelete = (id) => {
    const { activeAssignment, deleteAssignment, onAssign } = props

    // reset if activeAssignment
    if (activeAssignment && activeAssignment.id === id) {
      onAssign(null)
    }

    deleteAssignmentMutation({
      variables: { id },
    }).then(refetch)
  }

  const dispatchSort = debounce(() => {
    const ids = assignments.map((a) => a.id)
    const expected = ids.map((id, i) => ({ id, sortPosition: i }))

    sortAssignmentsMutation({
      variables: { ids },
      optimisticResponse: {
        sortAssignments: expected,
      },
    }).then(refetch)
  }, 500)

  const handleSort = (dragIndex, hoverIndex) => {
    const dragItem = assignments[dragIndex]

    setAssignments(
      update(assignments, {
        $splice: [
          [dragIndex, 1], // Remove the item at dragIndex
          [hoverIndex, 0, dragItem], // Insert the dragItem at hoverIndex
        ],
      })
    )

    dispatchSort()
  }
  const { metaReflection, onAssign, allowAssign } = props
  const showDrop = !metaReflection || (assignments && assignments.length === 0)
  const dropClass = isOver ? 'drop isOver' : 'drop'

  return (
    <ul className="queue">
      {assignments
        .filter((item) => item !== undefined)
        .map((a, i) => (
          <Assignment
            key={a.id}
            data={a}
            index={i}
            allowAssign={allowAssign}
            onAssign={onAssign}
            onCreate={handleCreate}
            onDelete={handleDelete}
            onSort={handleSort}
            canDrop={canDrop}
          />
        ))}
      {showDrop && (
        <li>
          <div ref={drop} className={dropClass}>
            Drag and drop a {metaReflection && 'final'} question here.
          </div>
        </li>
      )}
    </ul>
  )
}

export default ProjectQueue
